import Vue from "vue"
import { ActionTree, MutationTree, GetterTree } from "vuex"
import Vuex, { StoreOptions } from "vuex"
import { make } from "vuex-pathify"
import { login } from "@/store/modules/LoginStore"
import { properties } from "@/store/modules/PropertiesStore"
import { tenant } from "@/store/modules/TenantStore"
import { spaces } from "@/store/modules/SpacesStore"
import { maintenance } from "@/store/modules/MaintenanceStore"
import { fileUpload } from "@/store/modules/FileUploadStore"
import { support } from "@/store/modules/SupportStore"
import { loyalty } from "@/store/modules/LoyaltyStore"
import { order } from "@/store/modules/OrderStore"
import { amenities } from "@/store/modules/AmenitiesStore"
import { guestWaiver } from "@/store/modules/GuestWaiverStore"
import { payment } from "@/store/modules/PaymentStore"
import { guestInvite } from "@/store/modules/GuestInviteStore"
import { actionCode } from "@/store/modules/ActionCodeStore"
import { wallet } from "@/store/modules/WalletStore"
import Pathify from "./pathify"

Vue.use(Vuex)

export interface ConfirmationDialogInfo {
    title: string
    message: string
    action: () => void
    cancel?: () => void
    okTitle?: string
    okVariant?: string
    cancelTitle?: string
    cancelVariant?: string
    pills?: string[]
    reason?: boolean
}

export interface MultiConfirmationDialogInfo {
    title: string
    message: string
    firstAction: () => void
    firstActionTitle?: string
    firstActionVariant?: string
    secondAction: () => void
    secondActionTitle?: string
    secondActionVariant?: string
    cancel?: () => void
    cancelTitle?: string
    cancelVariant?: string
}

//===================================
// State
//===================================
export interface RootState {
    error?: Error
    loading: boolean
    loadingCount: number
    alert?: string
    appVersion: string
    confirmation?: ConfirmationDialogInfo
    multiConfirmation?: MultiConfirmationDialogInfo
    confirmationFeedback?: string
    showSave: boolean
    saveText: string
    showCancel: boolean
    saveDisabled: boolean
    cancelDisabled: boolean
}

const defaultState = () => {
    return <RootState>{
        error: undefined,
        loading: false,
        loadingCount: 0,
        alert: undefined,
        appVersion: process.env.PACKAGE_VERSION || "0",
        confirmation: undefined,
        multiConfirmation: undefined,
        confirmationFeedback: undefined,
        showSave: false,
        saveText: "Save",
        showCancel: false,
        saveDisabled: false,
        cancelDisabled: false
    }
}

export const state: RootState = defaultState()

//===================================
// Getters
//===================================
const getters: GetterTree<RootState, RootState> = {
    ...make.getters(state)
}

//===================================
// Actions
//===================================
export const actions: ActionTree<RootState, RootState> = {
    ...make.actions(state),

    confirmDanger({ dispatch }, confirmation: ConfirmationDialogInfo) {
        confirmation.okTitle = confirmation.okTitle ?? "Confirm"
        confirmation.okVariant = confirmation.okVariant ?? "outline-danger"
        confirmation.cancelTitle = confirmation.cancelTitle ?? "Cancel"
        confirmation.cancelVariant = confirmation.cancelVariant ?? "secondary"
        dispatch("setConfirmation", confirmation)
    },

    multiConfirm({ dispatch }, confirmation: MultiConfirmationDialogInfo) {
        confirmation.firstActionTitle = confirmation.firstActionTitle ?? "OK"
        confirmation.firstActionVariant = confirmation.firstActionVariant ?? "outline-primary"
        confirmation.secondActionTitle = confirmation.secondActionTitle ?? "OK"
        confirmation.secondActionVariant = confirmation.secondActionVariant ?? "outline-primary"
        confirmation.cancelTitle = confirmation.cancelTitle ?? "Cancel"
        confirmation.cancelVariant = confirmation.cancelVariant ?? "secondary"

        dispatch("setMultiConfirmation", confirmation)
    },

    setLoading({ commit }, value: boolean) {
        commit("adjustLoadingCount", value ? 1 : -1)
    }
}

//===================================
// Mutators
//===================================
const mutations: MutationTree<RootState> = {
    ...make.mutations(state),

    adjustLoadingCount(state, amount: number) {
        const newCount = state.loadingCount + amount
        state.loadingCount = Math.max(0, newCount)
        state.loading = state.loadingCount > 0
    }
}

//===================================
// Store
//===================================
const store: StoreOptions<RootState> = {
    plugins: [Pathify.plugin],
    strict: true,
    state: state,
    getters: getters,
    mutations: mutations,
    actions: actions,
    modules: {
        fileUpload,
        login,
        maintenance,
        properties,
        tenant,
        spaces,
        support,
        loyalty,
        order,
        amenities,
        guestWaiver,
        payment,
        guestInvite,
        actionCode,
        wallet
    }
}

export default new Vuex.Store<RootState>(store)
